/* payloadStore: the store equivalent of the old mutable global `STATE`
 * (service/review/index.html ~846, reassigned by load() ~3415-3439).
 *
 * loadPayload() is the pre-render half of old load(): parseInput ->
 * normalize -> buildState (F1's derive/parse.ts already isolates this pure
 * pipeline — see its buildState doc comment), then the same shell-chrome
 * DOM writes load() did (client name/descriptor/favicon, the run meta
 * line, the "as of" filename) BEFORE calling the render orchestrator —
 * same relative order as the original. `window.scrollTo(0,0)` runs last,
 * exactly like the original.
 *
 * DEVIATION (signature): the F2a brief specifies `loadPayload(raw:
 * unknown)`. parseInput's own contract (unchanged, F1) takes `text:
 * string` — every old call site already had a string in hand (`f.text()`,
 * `res.text()`, `JSON.stringify(st.result)`). `raw: unknown` is honored by
 * stringifying non-string input (`typeof raw === 'string' ? raw :
 * JSON.stringify(raw)`) before handing it to parseInput, so a caller may
 * pass either the raw fetched text (the normal case) or an
 * already-parsed object. `filename` is threaded as a second, optional
 * parameter — needed for the `#asof` DOM write and parseInput's own
 * brand-from-filename fallback, both present in the original.
 *
 * WS hook (F2d): the original wrapped the global `load` AFTER defining it
 * (`const _wsOrigLoad = load; load = function(text, filename){
 * _wsOrigLoad(text, filename); WS.init(); };`, ~4568-4569) so every load
 * re-probes the shared board for whichever client just got loaded.
 * loadPayload() calls `WS.init()` directly instead — same net effect
 * (full render pass first, board probe second, fire-and-forget/not
 * awaited exactly like the original wrapper), just without the
 * reassignment trick ESM exports don't support.
 */
import { buildState, normalize, parseInput } from '../derive/parse.ts';
import { coreAsset } from '../derive/exec.ts';
import { faviconUrl } from '../lib/format.ts';
import { actStatusStore, flaggedStore } from './ui.ts';
import { createStore } from './store.ts';
import { renderAll } from '../views/registry.ts';
import { WS } from './workspace.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

function emptyState(): AppPayloadState {
  return {
    brand: null, assets: [], edges: [], conflicts: [], coverage: {},
    dossier: null, serp: null, serps: [], opportunities: [],
    entity: null, knowledgePanel: null, wikidata: null, aiNarrative: null,
    impact: null, linkGraph: null, peers: null, synthesis: null, brandLogo: null,
    entityMap: null, skippedLayers: [], profileScope: null, planeStatus: null,
    generatedAt: null, runFile: null,
    selected: null, nodes: new Map(),
  };
}

export const payloadStore = createStore<AppPayloadState>(emptyState());

/* Load-generation guard (review #1 — payload-load race). Multiple async paths
 * fetch-then-loadPayload into this single global store (main.ts's legacy
 * ensureLegacyPayload, the cockpit's per-client run load, the refresh-history
 * click, a dropped file). If two overlap, the last FETCH to resolve wins and can
 * commit the WRONG run/client under the active route. Each loader takes a token
 * with beginLoad() BEFORE its fetch and commits only while isLoadCurrent(token)
 * — a superseded load discards its result instead of clobbering the newer one. */
let loadGeneration = 0;
export function beginLoad(): number { return ++loadGeneration; }
export function isLoadCurrent(token: number): boolean { return token === loadGeneration; }

export function loadPayload(raw: unknown, filename?: string): void {
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
  let parsed;
  try { parsed = parseInput(text, filename); }
  catch (err) { alert('Could not parse: ' + (err as Error).message); return; }
  const norm = normalize(parsed.payload);
  const state = buildState(parsed, norm);
  // The Copilot grounds on the loaded run by name (chat.ts) — the archive
  // filename is the only place that identity lives, so carry it in state.
  state.runFile = filename || null;
  payloadStore.set(state);
  actStatusStore.load(state.brand || '');
  flaggedStore.load(state.brand || '');
  const core = coreAsset(state);
  // The old topbar client-card + rail as-of chrome was removed with the CRM
  // shell port (Package 1) — these updates are now guarded so a payload load
  // (transitional research/report/cockpit views) never throws when that chrome
  // is absent. #runMeta still lives in the transitional §0 report section.
  const nameEl = document.getElementById('clientName');
  if (nameEl) nameEl.textContent = state.brand;
  const descEl = document.getElementById('clientDesc');
  if (descEl) descEl.textContent = (state.entity && state.entity.descriptor) || '';
  const cfe = document.getElementById('clientFav') as HTMLImageElement | null;
  const cc = cfe?.closest('.clientcard') as HTMLElement | null;
  if (cfe && cc) {
    if (state.brandLogo){ cfe.src = state.brandLogo; cfe.style.display = ''; cc.classList.add('haslogo'); }
    else {
      cc.classList.remove('haslogo');
      const cf = core && faviconUrl(core.url, 64);
      if (cf){ cfe.src = cf; cfe.style.display = ''; } else cfe.style.display = 'none';
    }
  }
  const polled = state.impact && state.impact.window ? `search window ${state.impact.window.start} → ${state.impact.window.end}` : '';
  const runMetaEl = document.getElementById('runMeta');
  if (runMetaEl) runMetaEl.textContent = `${state.assets.length} surfaces mapped${polled ? ' · ' + polled : ''}`;
  const asofEl = document.getElementById('asof');
  if (asofEl) asofEl.textContent = filename || '';
  renderAll(state);
  window.scrollTo(0, 0);
  WS.init();
}
